/**
 * DataLayer Ecommerce Events - Phase 5.1
 * Enhanced ecommerce event tracking for GA4/GTM
 * Part of Phase 5: DataLayer & AI Optimization
 */

(function() {
  'use strict';

  function debugLog() {
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      if (window.logger && typeof window.logger.debug === 'function') {
        window.logger.debug.apply(window.logger, arguments);
      }
    }
  }

  // Ensure DataLayer core is loaded
  if (!window.KT || !window.KT.DataLayer) {
    debugLog('[DataLayer Ecommerce] DataLayer core not loaded');
    return;
  }

  const DEFAULT_FEATURE_FLAGS_HOST = 'https://getsimilarproductshttp-309132447778.us-central1.run.app';

  function getFeatureFlagsBase() {
    const host = (window.__ENV__ && window.__ENV__.FEATURE_FLAGS_API_HOST) ||
                 DEFAULT_FEATURE_FLAGS_HOST ||
                 (window.__ENV__ && window.__ENV__.SHOPIFY_HOST) ||
                 window.location.origin;
    if (!host) {
      return '';
    }
    return host.replace(/\/+$/, '');
  }

  function buildFeatureFlagsUrl(path) {
    const base = getFeatureFlagsBase();
    if (!base || path.startsWith('http')) {
      return path;
    }
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  }

  const DataLayer = window.KT.DataLayer;
  const EcommerceEvents = {
    /**
     * Track product view
     */
    viewItem: function(product) {
      const item = DataLayer.formatProduct(product);
      if (!item) return;

      DataLayer.push({
        'event': 'view_item',
        'ecommerce': {
          'currency': 'GBP',
          'value': item.price,
          'items': [item]
        }
      });
    },

    /**
     * Track add to cart
     */
    addToCart: function(product, quantity) {
      const item = DataLayer.formatProduct(product);
      if (!item) return;

      item.quantity = parseInt(quantity || 1, 10);

      DataLayer.push({
        'event': 'add_to_cart',
        'ecommerce': {
          'currency': 'GBP',
          'value': item.price * item.quantity,
          'items': [item]
        }
      });
    },

    /**
     * Track remove from cart
     */
    removeFromCart: function(product) {
      const item = DataLayer.formatProduct(product);
      if (!item) return;

      DataLayer.push({
        'event': 'remove_from_cart',
        'ecommerce': {
          'currency': 'GBP',
          'value': item.price * item.quantity,
          'items': [item]
        }
      });
    },

    /**
     * Track view cart
     */
    viewCart: function(cart) {
      const cartData = DataLayer.formatCart(cart);
      if (!cartData) return;

      DataLayer.push({
        'event': 'view_cart',
        'ecommerce': cartData
      });
    },

  /*
   * begin_checkout, add_payment_info and purchase are deliberately NOT here.
   *
   * Theme JavaScript does not run on Shopify-hosted checkout, so the
   * beginCheckout/addPaymentInfo/purchase helpers that used to sit in this object
   * had zero callers and always would have — they made the checkout funnel look
   * instrumented when nothing could ever fire them. Those stages now come from
   * extensions/kt-checkout-funnel-pixel, which runs inside the checkout sandbox.
   *
   * The theme's own checkout signal is kt_journey_checkout_intent
   * (assets/cart-datalayer-integration.js) — a CLICK on checkout, which is a
   * different thing from arriving at it.
   */
    /**
     * Track view item list (collection/category)
     */
    viewItemList: function(listName, items) {
      if (!items || !Array.isArray(items)) return;

      const formattedItems = items.map(item => DataLayer.formatProduct(item));

      DataLayer.push({
        'event': 'view_item_list',
        'ecommerce': {
          'item_list_id': listName || 'collection',
          'item_list_name': listName || 'Collection',
          'items': formattedItems
        }
      });
    },

    /**
     * Track search
     */
    search: function(searchTerm, results) {
      DataLayer.push({
        'event': 'search',
        'search_term': searchTerm || '',
        'search_results': parseInt(results || 0, 10)
      });
    },

    /**
     * Track select item (from list)
     */
    selectItem: function(product, listName) {
      const item = DataLayer.formatProduct(product);
      if (!item) return;

      DataLayer.push({
        'event': 'select_item',
        'ecommerce': {
          'item_list_id': listName || 'collection',
          'item_list_name': listName || 'Collection',
          'items': [item]
        }
      });
    },

    /**
     * Track promotion view
     */
    viewPromotion: function(promotion) {
      DataLayer.push({
        'event': 'view_promotion',
        'ecommerce': {
          'promotions': [{
            'promotion_id': promotion.id || null,
            'promotion_name': promotion.name || null,
            'creative_name': promotion.creative || null,
            'creative_slot': promotion.slot || null
          }]
        }
      });
    },

    /**
     * Track promotion click
     */
    selectPromotion: function(promotion) {
      DataLayer.push({
        'event': 'select_promotion',
        'ecommerce': {
          'promotions': [{
            'promotion_id': promotion.id || null,
            'promotion_name': promotion.name || null,
            'creative_name': promotion.creative || null,
            'creative_slot': promotion.slot || null
          }]
        }
      });
    }
  };

  // Expose to window
  window.KT = window.KT || {};
  window.KT.EcommerceEvents = EcommerceEvents;

  /**
   * Check if dataLayer feature is enabled
   */
  async function checkFeatureEnabled() {
    // Check localStorage for feature flag override, default to enabled
    // Note: Remote feature flag API is not yet deployed, so we skip the network call
    // to avoid 405 errors on every page load. Re-enable when API is ready.
    const stored = localStorage.getItem('feature_analytics_datalayer');
    return stored !== 'false'; // Default to enabled
  }

  // Auto-track common events if Shopify objects are available (with feature flag check)
  if (window.Shopify) {
    checkFeatureEnabled().then(enabled => {
      if (!enabled) {
        debugLog('[DataLayer Ecommerce] Feature disabled via feature flag');
        return;
      }

      // Track product view if on product page
      if (window.Shopify.theme && window.Shopify.theme.product) {
        document.addEventListener('DOMContentLoaded', function() {
          setTimeout(function() {
            EcommerceEvents.viewItem(window.Shopify.theme.product);
          }, 500);
        });
      }

      // Track cart view if on cart page
      if (window.location.pathname.includes('/cart')) {
        document.addEventListener('DOMContentLoaded', function() {
          // Fetch cart data and track
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              EcommerceEvents.viewCart(cart);
            })
            .catch(err => debugLog('[DataLayer] Failed to fetch cart:', err));
        });
      }
    });
  }

})();
